import { trackCreativeAction } from 'analytics';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import ChatAttachmentTile, { ChatAttachmentTileState } from 'components/Widget/ChatAttachmentTile';
import FontSelector from 'components/Widget/Input/FontSelector';
import IconPackSelector from 'components/Widget/Input/IconPackSelector';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import { CreateDesignSystemResponse } from 'dragonmouse-shared/api/forge';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import { selectScenes } from 'redux/db/reducers';
import { setPendingDsPlan } from 'redux/mode/actions';
import { selectPendingDsPlan } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import store from 'redux/store';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import { FileUploadCallbacks, UploadTracker, onFileDropped, onUploadFileDialog } from 'utils';
import * as Base from '../styles';
import SceneLinkToggle from './SceneLinkToggle';
import * as S from './styles';

const DesignSystemPlanReview = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const theme = useTheme();
	const [searchParams] = useSearchParams();
	const sceneId = searchParams.get('sceneId');
	const organizationId = searchParams.get('organizationId');
	const [loadingMessage, setLoadingMessage] = useState<string>('');
	const [linkToScene, setLinkToScene] = useState<boolean>(true);
	const isNavigatingToDs = useRef(false);

	const plan = useSelector((r: RootReducer) => selectPendingDsPlan(r.mode));
	const scenes = useSelector((r: RootReducer) => selectScenes(r.db));
	const scene = sceneId ? scenes.find((s) => s.id === sceneId) : null;

	// Redirect to input if no plan (but not if we're navigating to a new DS)
	useEffect(() => {
		if (!plan && !isNavigatingToDs.current) {
			navigate(routes.createDesignSystem(sceneId || undefined, organizationId || undefined));
		}
	}, [plan, navigate, sceneId, organizationId]);

	const [loadingAttachments, setLoadingAttachments] = useState<UploadTracker[]>([]);
	const [isDragging, setIsDragging] = useState<boolean>(false);

	const fileCallbacks: FileUploadCallbacks = useMemo(
		() => ({
			onLoadingStart: (uploadTracker: UploadTracker) => {
				setLoadingAttachments((prev) => [...prev, uploadTracker]);
			},
			onLoadingEnd: (uniqueId: string) => {
				setLoadingAttachments((prev) => prev.filter((a) => a.uniqueId !== uniqueId));
			},
			onSuccess: (attachment: Attachment) => {
				const currentPlan = store.getState().mode.pendingDsPlan;
				if (!currentPlan) return;
				if (currentPlan.attachments.some((a) => a.storagePath === attachment.storagePath)) {
					toast.warning('This file is already attached');
					return;
				}
				dispatch(
					setPendingDsPlan({
						...currentPlan,
						attachments: [...currentPlan.attachments, attachment],
					})
				);
			},
			onError: (error: Error) => {
				console.error('Error uploading file:', error);
			},
		}),
		[dispatch]
	);

	if (!plan) return null;

	const updatePlan = (updates: Partial<typeof plan>) => {
		dispatch(setPendingDsPlan({ ...plan, ...updates }));
	};

	const onRemoveAttachment = (storagePath: string) => {
		updatePlan({ attachments: plan.attachments.filter((a) => a.storagePath !== storagePath) });
	};

	const onAddAttachment = () => {
		onUploadFileDialog(fileCallbacks, true);
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer.types.includes('Files')) {
			setIsDragging(true);
		}
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!e.currentTarget.contains(e.relatedTarget as Node)) {
			setIsDragging(false);
		}
	};

	const onDrop = async (e: React.DragEvent) => {
		setIsDragging(false);
		if (loadingAttachments.length > 0) {
			toast.info('Upload in progress. Please wait.');
			return;
		}
		await onFileDropped(fileCallbacks)(e);
	};

	const onNewDesignSystem = async () => {
		try {
			setLoadingMessage('creating design system...');
			trackCreativeAction('new_design_system');
			trackCreativeAction('new_ds_chat');
			const res: CreateDesignSystemResponse = await forge.createDesignSystem.withToast()({
				plan,
				sceneId: linkToScene && sceneId ? sceneId : undefined,
				organizationId: organizationId || undefined,
			});
			isNavigatingToDs.current = true; // Prevent redirect effect from firing
			dispatch(setPendingDsPlan(null)); // Clear plan after creation
			setLoadingMessage('');
			navigate(routes.designSystem(res.designSystem.id, res.designSystem.title));
		} catch (e) {
			setLoadingMessage('');
		}
	};

	return (
		<Base.Base>
			{loadingMessage && <LoadingOverlay text={loadingMessage} semiTransparent />}
			<S.ScrollContainer>
				<S.ContentWrapper>
					<Base.Title>Review your design system plan</Base.Title>
					<S.PlanReview>
						<S.PlanCard>
							<S.CardTitle>Title</S.CardTitle>
							<S.Input
								value={plan.title}
								onChange={(e) => updatePlan({ title: e.target.value })}
								placeholder="Design system name"
							/>
						</S.PlanCard>

						<S.PlanCard>
							<S.CardTitle>Description</S.CardTitle>
							<S.TextArea
								value={plan.description}
								onChange={(e) => updatePlan({ description: e.target.value })}
								placeholder="Describe your design system..."
							/>
						</S.PlanCard>
						<S.DropZoneCard
							isDragging={isDragging}
							onDragOver={onDragOver}
							onDragLeave={onDragLeave}
							onDrop={onDrop}
						>
							<S.CardTitle>Attachments</S.CardTitle>
							<S.CardDescription>
								Reference images and files to guide design system generation. Drag and drop here, or
								click "Add files" below to choose files on your computer..
							</S.CardDescription>
							<S.AttachmentList>
								{plan.attachments.map((attachment) => (
									<ChatAttachmentTile
										key={attachment.storagePath}
										attachment={attachment}
										onDelete={() => onRemoveAttachment(attachment.storagePath)}
										state={ChatAttachmentTileState.REGULAR}
									/>
								))}
								{loadingAttachments.map((tracker) => (
									<ChatAttachmentTile
										key={tracker.uniqueId}
										attachment={{
											filename: tracker.filename,
											contentType: tracker.contentType,
											storagePath: `loading-${tracker.uniqueId}`,
											size: tracker.size,
										}}
										state={ChatAttachmentTileState.LOADING}
										previewUrl={tracker.previewUrl}
									/>
								))}
								<S.AddAttachmentButton type="button" onClick={onAddAttachment}>
									+ Add files
								</S.AddAttachmentButton>
							</S.AttachmentList>
							{isDragging && (
								<S.DragOverlay>
									<S.DragOverlayContent>
										<Icon
											type={IconType.ATTACHMENT}
											width={32}
											height={32}
											color={theme.gray[1000]}
										/>
										<S.DragOverlayText>Drop files to attach</S.DragOverlayText>
									</S.DragOverlayContent>
								</S.DragOverlay>
							)}
						</S.DropZoneCard>

						<S.PlanCard>
							<S.CardTitle>Icon Pack</S.CardTitle>
							<S.CardDescription>Choose the icon library for your design system.</S.CardDescription>
							<IconPackSelector
								value={plan.iconPack}
								onChange={(iconPack) => updatePlan({ iconPack })}
							/>
						</S.PlanCard>

						<S.PlanCard>
							<S.CardTitle>Fonts</S.CardTitle>
							<S.CardDescription>
								Configure Google Fonts for your design system. Select font families, weights, and
								fallbacks.
							</S.CardDescription>
							<FontSelector value={plan.fonts} onChange={(fonts) => updatePlan({ fonts })} />
						</S.PlanCard>

						<S.PlanCard>
							<S.CardTitle>Components</S.CardTitle>
							<S.CardDescription>
								Key components that will be included in your design system.
							</S.CardDescription>
							<S.TagList>
								{plan.keyComponents.map((comp, i) => (
									<S.Tag key={i}>
										{comp}
										<S.RemoveTag
											type="button"
											onClick={() =>
												updatePlan({
													keyComponents: plan.keyComponents.filter((_, index) => index !== i),
												})
											}
										>
											×
										</S.RemoveTag>
									</S.Tag>
								))}
								<S.AddTagInput
									placeholder="Add component..."
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											const val = e.currentTarget.value.trim();
											if (val) {
												updatePlan({ keyComponents: [...plan.keyComponents, val] });
												e.currentTarget.value = '';
											}
										}
									}}
								/>
							</S.TagList>
						</S.PlanCard>

						{sceneId && scene && (
							<SceneLinkToggle
								sceneName={scene.title}
								checked={linkToScene}
								onChange={setLinkToScene}
							/>
						)}

						<S.ButtonRow>
							<S.SecondaryButton
								onClick={() => navigate(routes.createDesignSystem(sceneId || undefined))}
							>
								Back
							</S.SecondaryButton>
							<S.PrimaryButton onClick={onNewDesignSystem}>Create Design System</S.PrimaryButton>
						</S.ButtonRow>
					</S.PlanReview>
				</S.ContentWrapper>
			</S.ScrollContainer>
		</Base.Base>
	);
};

export default DesignSystemPlanReview;
